import { computed, makeObservable, observable, runInAction } from "mobx";
import { ViewModelBase } from "@shoothill/core";
import { StudentSessionAQACertificatesModel, StudentSessionAQACertificatesModelValidator } from "./StudentSessionAQACertificatesModel";
import { RelayCommand } from "Application";
import { StudentSessionAQACertificateViewModel } from "./Shared/StudentSessionAQACertificateViewModel";
import { StudentSessionAQACertificateModel } from "./Shared/StudentSessionAQACertificateModel";

export class StudentSessionAQACertificatesViewModel extends ViewModelBase<StudentSessionAQACertificatesModel> {
    public _AQACertificateToAdd: StudentSessionAQACertificateViewModel | null = null;
    public _AQACertificateToEdit: StudentSessionAQACertificateViewModel | null = null;
    public _AQACertificateToDelete: StudentSessionAQACertificateViewModel | null = null;
    constructor(model: StudentSessionAQACertificatesModel) {
        super(model);
        this.setValidator(new StudentSessionAQACertificatesModelValidator());
        makeObservable(this, {
            _AQACertificateToAdd: observable,
            _AQACertificateToEdit: observable,
            _AQACertificateToDelete: observable,
            certificates: computed,
            showTable: computed,
            showAddModal: computed,
            showEditModal: computed,
            showDeleteModal: computed,
        });
    }

    //#region computed values
    public get certificates() {
        // Map the model's certificates to view models on-the-fly
        const activeCerts = this.model.certificates.slice().filter((cert) => !cert.isDeleted);
        return activeCerts.map((cert) => new StudentSessionAQACertificateViewModel(cert));
    }

    public get showTable() {
        return this.model.certificates.length > 0 && this.model.certificates.some((cert) => !cert.isDeleted);
    }

    public get showAddModal() {
        return this._AQACertificateToAdd !== null;
    }

    public get showEditModal() {
        return this._AQACertificateToEdit !== null;
    }

    public get showDeleteModal() {
        return this._AQACertificateToDelete !== null;
    }
    //#endregion

    //#region Commands
    public addStudentSessionAQACertificateCommand = new RelayCommand(() => {
        runInAction(() => {
            this._AQACertificateToAdd = new StudentSessionAQACertificateViewModel(new StudentSessionAQACertificateModel());
            this._AQACertificateToAdd.viewMode.addMode();
        });
    });

    public editStudentSessionAQACertificateCommand = new RelayCommand((v: any) => {
        runInAction(() => {
            const originalModel = (v.original as StudentSessionAQACertificateViewModel).model;
            const model = new StudentSessionAQACertificateModel();
            model.fromResponse(originalModel);
            model.KEY = originalModel.KEY;
            this._AQACertificateToEdit = new StudentSessionAQACertificateViewModel(model);
            this._AQACertificateToEdit.viewMode.editMode();
        });
    });

    public deleteStudentSessionAQACertificateCommand = new RelayCommand((v: StudentSessionAQACertificateViewModel) => {
        runInAction(() => {
            this._AQACertificateToDelete = v;
        });
    });

    public confirmStudentSessionAQAAddCommand = new RelayCommand(() => {
        runInAction(() => {
            if (this._AQACertificateToAdd) {
                // Add directly to the model's certificates collection
                this.model.certificates.push(this._AQACertificateToAdd.model);
                this._AQACertificateToAdd = null;
            }
        });
    });

    public confirmStudentSessionAQAEditCommand = new RelayCommand(() => {
        runInAction(() => {
            if (this._AQACertificateToEdit) {
                //find the edited model
                const editedModel = this.model.certificates.find((cert) => cert.KEY === this._AQACertificateToEdit?.model.KEY);
                if (editedModel) {
                    // Update the edited model with the new values
                    editedModel.fromResponse(this._AQACertificateToEdit.model);
                }
                this._AQACertificateToEdit = null;
            }
        });
    });

    public confirmStudentSessionAQADeleteCommand = new RelayCommand(() => {
        runInAction(() => {
            if (this._AQACertificateToDelete) {
                // Find the certificate in the model and mark it as deleted
                const certIndex = this.model.certificates.findIndex((cert) => cert.KEY === this._AQACertificateToDelete?.model.KEY);

                if (certIndex >= 0) {
                    this.model.certificates[certIndex].isDeleted = true;
                }

                this._AQACertificateToDelete = null;
            }
        });
    });

    public cancelAddCommand = new RelayCommand(() => {
        this._AQACertificateToAdd = null;
    });

    public cancelEditCommand = new RelayCommand(() => {
        this._AQACertificateToEdit = null;
    });

    public cancelDeleteCommand = new RelayCommand(() => {
        this._AQACertificateToDelete = null;
    });
    //#endregion
}
